// frontend/src/components/CircleSlimBanner.jsx

import React from "react";
import { useNavigate } from "react-router-dom";
import { UploadSimple, LinkSimple, X } from "phosphor-react";
import { DEFAULT_CIRCLE_IMAGE } from "../utils/blank-image";

export default function CircleSlimBanner({
  circle,
  isAdmin,
  logoBuster,
  locText,
  website,
  flag,
  openEditAllModal,
  openLogoPicker,
  logoFileInputRef,
  onLogoFileChosen,
  circleLogoUrl,
  onDeleteLogo,
}) {
  const navigate = useNavigate();

  const logoSrc = `${circleLogoUrl(circle)}${
    logoBuster ? `?v=${logoBuster}` : ""
  }`;

  const handleEditDetails = () => {
    const isMobile = window.matchMedia("(max-width: 639px)").matches;

    if (isMobile) {
      navigate(`/circles/${circle.id}/edit-details`);
      return;
    }

    openEditAllModal();
  };

  return (
    <div className="relative flex items-start gap-4 rounded-2xl bg-blue-100 p-5">
      {isAdmin && (
        <button
          type="button"
          onClick={handleEditDetails}
          className="absolute right-3 top-3 rounded-full bg-blue-600 text-white shadow-sm leading-none hover:bg-blue-700 [min-height:0] [min-width:0]"
          title="Edit circle details"
          style={{
            fontSize: 11,
            padding: "4px 8px",
            lineHeight: 1,
          }}
        >
          Edit
        </button>
      )}

      <div className="relative">
        <img
          key={circle?.logo_url || circle?.image_url || "default"}
          src={logoSrc}
          alt={`${circle?.name || "Circle"} logo`}
          className="h-20 w-20 rounded-full border border-gray-200 object-cover md:h-24 md:w-24"
          onError={(e) => {
            e.currentTarget.src = DEFAULT_CIRCLE_IMAGE;
          }}
        />

        {isAdmin && (circle?.logo_url || circle?.image_url) && (
          <button
            type="button"
            onClick={onDeleteLogo}
            className="absolute right-1 top-0 flex !h-6 !w-6 translate-x-1 translate-y-1 items-center justify-center overflow-hidden rounded-full bg-white !p-0 text-gray-500 ring-1 ring-gray-200 hover:bg-red-100 hover:text-red-600 hover:ring-red-700 [min-height:0] [min-width:0] leading-none"
            title="Delete logo"
            style={{
              width: 24,
              height: 24,
              lineHeight: 1,
            }}
          >
            <X size={12} weight="bold" />
          </button>
        )}

        {isAdmin && (
          <>
            <button
              type="button"
              onClick={openLogoPicker}
              className="absolute bottom-1 right-1 flex !h-6 !w-6 translate-x-1 translate-y-1 items-center justify-center overflow-hidden rounded-full bg-blue-500 !p-0 text-white ring-1 ring-blue-200 hover:bg-blue-700 [min-height:0] [min-width:0] leading-none"
              title="Change logo"
              style={{
                width: 24,
                height: 24,
                lineHeight: 1,
              }}
            >
              <UploadSimple size={12} />
            </button>

            <input
              ref={logoFileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={onLogoFileChosen}
            />
          </>
        )}
      </div>

      <div className="ml-1 min-w-0 flex-1 md:ml-2">
        <h1 className="text-[20px] font-semibold leading-tight md:text-2xl">
          {circle?.name || ""}
        </h1>

        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-gray-600">
          {flag && <span className="text-base leading-none">{flag}</span>}
          {locText && <span>{locText}</span>}
        </div>

        {website && (
          <div>
            <a
              href={website.startsWith("http") ? website : `https://${website}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 break-all text-sm text-blue-600 hover:underline"
            >
              <LinkSimple size={16} />

              {website.replace(/^https?:\/\//, "")}
            </a>
          </div>
        )}

        <p className="ml-1 mt-1 text-sm text-gray-900">
          {circle?.description || "Your space to grow, share, and connect."}
        </p>
      </div>
    </div>
  );
}